import * as React from 'react';
import {useState} from 'react';
import {css} from "emotion";
import {NotificationLevel, ReleaseNotesGroup} from "@components/version-update/VersionUpdate";

const bannerStyle = css`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 12px 20px;
    display: flex;
    flex-direction: column;
    z-index: 10000;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    animation: slideDown 0.3s ease-out;

    @keyframes slideDown {
        from {
            transform: translateY(-100%);
        }
        to {
            transform: translateY(0);
        }
    }
`;

const rowStyle = css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
`;

const contentStyle = css`
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    min-width: 0;
`;

const iconStyle = css`
    font-size: 20px;
`;

const messageStyle = css`
    font-size: 14px;
    font-weight: 500;
`;

const buttonsStyle = css`
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
`;

const buttonBaseStyle = css`
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    transition: all 0.2s ease;

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }
`;

const reloadButtonStyle = css`
    ${buttonBaseStyle}
    background: white;
    color: #667eea;
`;

const dismissButtonStyle = css`
    ${buttonBaseStyle}
    background: rgba(255, 255, 255, 0.2);
    color: white;

    &:hover {
        background: rgba(255, 255, 255, 0.3);
    }
`;

const whatsNewToggleStyle = css`
    ${buttonBaseStyle}
    background: rgba(255, 255, 255, 0.15);
    color: white;

    &:hover {
        background: rgba(255, 255, 255, 0.28);
    }
`;

const notesPanelStyle = css`
    margin-top: 10px;
    background: rgba(255, 255, 255, 0.96);
    color: #1f2430;
    border-radius: 8px;
    padding: 12px 14px;
    max-height: 45vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
`;

const notesGroupStyle = css`
    &:not(:first-child) {
        margin-top: 10px;
        padding-top: 10px;
        border-top: 1px solid #e6eaff;
    }
`;

const notesVersionStyle = css`
    font-size: 11px;
    font-weight: 700;
    color: #7a83c4;
    margin: 0 0 3px;
    font-family: monospace;
`;

const notesListStyle = css`
    margin: 0;
    padding-left: 18px;
    font-size: 13px;
    line-height: 1.6;

    li {
        margin-bottom: 4px;
    }
`;

type Props = {
    mismatchDetected: boolean
    dismissed: boolean
    notificationLevel: NotificationLevel
    releaseNotes: ReleaseNotesGroup[] | null
    forceReload: () => void
    dismissNotification: () => void
};

function VersionUpdateBanner({mismatchDetected, dismissed, notificationLevel, releaseNotes, forceReload, dismissNotification}: Props) {
    const [expanded, setExpanded] = useState(false);

    // Only show banner if:
    // 1. Mismatch detected
    // 2. Not dismissed
    // 3. Notification level is subtle or moderate
    if (!mismatchDetected ||
        dismissed ||
        notificationLevel === NotificationLevel.AGGRESSIVE ||
        notificationLevel === NotificationLevel.NONE) {
        return null;
    }

    const hasNotes = !!releaseNotes && releaseNotes.length > 0;

    return (
        <div className={bannerStyle}>
            <div className={rowStyle}>
                <div className={contentStyle}>
                    <span className={iconStyle}>🔄</span>
                    <span className={messageStyle}>
                        A new version is available. Reload to get the latest features and fixes.
                    </span>
                </div>
                <div className={buttonsStyle}>
                    {hasNotes &&
                        <button className={whatsNewToggleStyle} onClick={() => setExpanded(prev => !prev)}>
                            {expanded ? "Hide" : `What's new${releaseNotes ? ` (${releaseNotes.length})` : ''}`} {expanded ? '▴' : '▾'}
                        </button>
                    }
                    <button className={reloadButtonStyle} onClick={forceReload}>
                        Reload Now
                    </button>
                    <button className={dismissButtonStyle} onClick={dismissNotification}>
                        Later
                    </button>
                </div>
            </div>

            {hasNotes && expanded &&
                <div className={notesPanelStyle}>
                    {releaseNotes.map(group => (
                        <div key={group.version} className={notesGroupStyle}>
                            <div className={notesVersionStyle}>{group.version}</div>
                            <ul className={notesListStyle}>
                                {group.notes.map((note, i) => (
                                    <li key={i}>{note}</li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            }
        </div>
    );
}

export default VersionUpdateBanner
